import { Outlet } from 'react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { useScrollResetOnNavigate } from '../lib/scroll-reset';
import { useKeyboardOverlayPad } from '../lib/use-keyboard-overlay-pad';
import { useScrollRestoration } from '../lib/scroll-restoration';
import { trackOnce } from '../lib/funnel';
import { usePlansWarmup } from '../lib/use-plans-warmup';
import { sessionContext } from '../lib/session-context';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { Footer } from './footer';
import { OnboardingModal } from './onboarding-modal';
import { EmailConfirmNotice } from './email-confirm-notice';
import { OfflineBanner } from './offline-banner';

/**
 * m16 sidebar app shell. Responsive:
 *  - `lg` and up: the persistent 240px sidebar + a topbar with a scrollable
 *    content area (the original desktop layout, unchanged).
 *  - below `lg`: the sidebar is hidden and the grid collapses to a single
 *    column; the topbar exposes a hamburger that opens the same nav as a left
 *    off-canvas drawer (see `Topbar` → `MobileNav`). Content goes full-width
 *    with tighter padding.
 *
 * Used as the public layout route (renders <Outlet/>) and by the SSR entry
 * (which passes the matched route as `children`).
 *
 * SSR-safe: no window/document access at render. `min-w-0` on the content
 * column lets it shrink instead of overflowing the viewport.
 */
export function AppLayout({ children }: { children?: ReactNode }) {
  // `<main>` is the scroll container (see its `overflow-y-auto` below), so a
  // route change leaves the reader wherever the previous page was scrolled to
  // — most visibly on mobile, where opening a tender landed you mid-article.
  // Neither the browser nor react-router can help here: both only ever touch
  // WINDOW scroll, which this shell never uses. Hence the pair below — reset on
  // a forward navigation, restore the entry's own offset on Back/Forward. They
  // are mutually exclusive by navigation type and never fight.
  const mainRef = useRef<HTMLElement>(null);
  // In-app browsers that open the keyboard without resizing anything: pad the
  // shell's scroller so the covered half can be scrolled into view (see hook).
  useKeyboardOverlayPad(mainRef);
  useScrollResetOnNavigate(mainRef);
  useScrollRestoration(mainRef);
  // Top of the conversion funnel: one per browser session (guarded). Carries the
  // acquisition context — referrer host, landing path, source markers — because
  // without it the events table cannot answer where a visitor came from, and
  // every "Meta vs Telegram vs search" question is guesswork.
  useEffect(() => trackOnce('session_start', sessionContext()), []);
  // The price list is fetched a few seconds into the session — see the hook.
  usePlansWarmup();

  return (
    <div className="grid h-[100dvh] grid-cols-1 overflow-hidden lg:grid-cols-[240px_1fr]">
      <Sidebar className="hidden lg:flex" />
      <div className="flex h-[100dvh] min-w-0 flex-col overflow-hidden bg-bone">
        <Topbar />
        {/* «Нет соединения с интернетом» while offline (22.10) — under the
            topbar, outside the scroller, so it stays put while the page scrolls. */}
        <OfflineBanner />
        <main
          ref={mainRef}
          // globals.css pads this scroller by a fixed bottom bar's height when a
          // page renders one (`data-sticky-cta`, the lot page on phones).
          data-shell-scroller=""
          className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-4 pb-[calc(2rem+var(--kb-pad,0px))] pt-5 sm:px-6"
        >
          {/* The ИНН form used to sit here, on EVERY page. Now that the catalog
              carries the onboarding strip — which asks for the same ИНН — that
              meant two prompts for one field stacked on one screen. The form
              lives on the dashboard (routes/dashboard.tsx), and the strip links
              to it. One ask, one place to answer it. */}
          {/* Where a letter's «Подтвердить почту» lands (`?emailConfirm=`) —
              one line, any page, gone after a reload. */}
          <EmailConfirmNotice />
          <div className="flex-1">{children ?? <Outlet />}</div>
          <Footer />
        </main>
      </div>
      <OnboardingModal />
    </div>
  );
}
